import type { RouteLocationRaw } from 'vue-router';
import type { AvailableAccounts } from '~/types/auth';
import { consoleLog } from '@hm-common/utils/utils';
import { FetchError } from 'ofetch';

const DEV_FORCE_FULL_ACCOUNT_COMPLETION = false;

interface AvailableAccountsState {
	accounts: AvailableAccounts['accounts'];
	callbackUrl: string;
	requiredAccountType?: UserAccountType;
	needsFullAccount?: boolean;

	applicationCode?: string;
	wantedAccountCode?: string;
}

export function useAvailableAccounts() {
	const state = useState<AvailableAccountsState>('availableAccounts');
	const redirectLoading = ref(false);

	const { $api, $toast } = useNuxtApp();

	const setAccounts = (data: AvailableAccountsState) => {
		if (DEV_FORCE_FULL_ACCOUNT_COMPLETION && import.meta.env.DEV) {
			data.accounts.forEach((account) => {
				account.reviewAccountOnNextLogin = true;
			});
		}

		state.value = data;
	};

	async function redirect(accountCode?: string) {
		redirectLoading.value = true;

		if (import.meta.env.DEV) {
			consoleLog('Available accounts state before redirect:', state.value);
		}

		try {
			// Flow if the user has no accounts
			if (state.value.accounts.length === 0) {
				let url: RouteLocationRaw;
				// If personal account is required, redirect to personal onboarding
				if (state.value.requiredAccountType === 'Personal') {
					// Redirect to full personal onboarding if needed
					if (state.value.needsFullAccount) {
						url = useCamPortalURL('onboarding-personal-full');
					}
					// Otherwise, redirect to personal onboarding
					else {
						url = useCamPortalURL('onboarding-personal');
					}
				}
				// Redirect to business onboarding if business account is required
				else if (state.value.requiredAccountType === 'Business') {
					url = useCamPortalURL('onboarding-business');
				}
				// Redi
				else if (state.value.needsFullAccount) {
					url = useCamPortalURL('onboarding-full');
				}
				else {
					// Otherwise, redirect to general onboarding
					url = useCamPortalURL('onboarding');
				}

				const uri = new URL(url as string);
				if (state.value.callbackUrl) {
					uri.searchParams.delete('callbackUrl');
					uri.searchParams.append('callbackUrl', state.value.callbackUrl);
				}

				setApplicationCode(uri);

				state.value.applicationCode = 'cam';

				await getCodeAndRedirect(uri);
				return;
			}

			let account = state.value.accounts.find(a => a.code === accountCode);
			if (!account) {
				account = state.value.accounts[0];
			}

			// Flow if user has partial account, but target application requires full account
			if (state.value.needsFullAccount && !account?.isFullAccount && account?.type === 'Personal') {
				const uri = new URL(useCamPortalURL('onboarding-personal-complete') as string);

				await handleCallbackUrlForCamPortal(uri, account?.code);
				return;
			}
			// Flow if user needs to review their account - eg. for migrated accounts
			else if (account?.reviewAccountOnNextLogin) {
				const uri = new URL(useCamPortalURL(account.type === 'Business' ? 'review-business' : 'review-personal') as string);

				await handleCallbackUrlForCamPortal(uri, account?.code);
				return;
			}

			// Normal flow - redirect to callback url with one-off access code
			const uri = new URL(state.value.callbackUrl);
			await getCodeAndRedirect(uri, account?.code);
		}
		catch (error) {
			$toast.error(parseApiErrorMessage(error));
			if (error instanceof FetchError && error.statusCode === 404) {
				window.location.replace('logout');
			}
		}
		finally {
			redirectLoading.value = false;
		}
	}

	async function handleCallbackUrlForCamPortal(url: URL, accountCode?: string) {
		if (state.value.callbackUrl) {
			url.searchParams.delete('callbackUrl');
			url.searchParams.append('callbackUrl', state.value.callbackUrl);
		}

		setApplicationCode(url);

		state.value.applicationCode = 'cam';

		await getCodeAndRedirect(url, accountCode);
	}

	function setApplicationCode(uri: URL) {
		if (state.value.applicationCode) {
			uri.searchParams.delete('applicationCode');
			uri.searchParams.append('applicationCode', state.value.applicationCode);
		}
	}

	async function getCodeAndRedirect(url: URL, accountCode?: string) {
		// remove code from url so new code doesn't get appended
		url.searchParams.delete('code');

		const { value } = await $api<ValueResponse<GetCodeResponse>>('/api/auth/one-off-access-code', {
			method: 'POST',
			body: {
				callbackUrl: url.toString(),
				accountCode,
				applicationCode: state.value.applicationCode,
			},
		});

		if (import.meta.env.DEV) {
			consoleLog('Redirect URL:', value.callbackUrl);
		}

		window.location.replace(value.callbackUrl);
	}

	return {
		accounts: computed(() => state.value?.accounts),
		redirect,
		redirectLoading: computed(() => redirectLoading.value),
		setAccounts,
		wantedAccountCode: computed(() => state.value?.wantedAccountCode),
		applicationCode: computed(() => state.value?.applicationCode ?? 'cam'),
		state: readonly(state.value),
	};
}

export interface GetCodeResponse {
	callbackUrl: string;
}
